import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect, useMemo } from 'react';

function OrderBook({ futureasset }) {
  const getDecimals = (price) => {
    const value = Number(price);
    if (!Number.isFinite(value)) return 2;
    if (value >= 1000) return 2;
    if (value >= 1) return 4;
    if (value >= 0.1) return 5;
    if (value >= 0.01) return 6;
    if (value >= 0.001) return 7;
    if (value >= 0.0001) return 8;
    return 10;
  };
  const getPriceStep = (price) => {
    const value = Number(price);
    if (!Number.isFinite(value) || value <= 0) {
      return 0.01;
    }
    const percentage = Math.random() * 0.0008 + 0.0002;
    return value * percentage;
  };

  const generatePrice = (marketPrice, direction = 1, distance = 1) => {
    const price = Number(marketPrice);

    if (!Number.isFinite(price) || price <= 0) {
      return '0.00';
    }

    const step = getPriceStep(price);

    const randomVariation =
      step * (0.5 + Math.random());

    const newPrice =
      price + (randomVariation * direction * distance);

    const decimals = getDecimals(price);

    return Math.max(newPrice, 0.00000001).toFixed(decimals);
  };

  /*
   * Generate realistic quantity based on the price.
   */
  const generateQuantity = (marketPrice) => {
    const price = Number(marketPrice);

    if (!Number.isFinite(price) || price <= 0) {
      return '0.0000';
    }

    /*
     * Larger-priced coins naturally have smaller quantities.
     * Lower-priced coins naturally have larger quantities.
     */
    const baseQuantity = 500 / price;

    const variation =
      baseQuantity * (Math.random() * 0.15 - 0.075);

    const quantity = Math.max(
      baseQuantity + variation,
      0.00000001
    );

    /*
     * Quantity precision adapts to the asset.
     */
    let decimals = 4;

    if (price < 1) decimals = 6;
    if (price < 0.1) decimals = 8;
    if (price < 0.001) decimals = 10;

    return quantity.toFixed(decimals);
  };


  const createRows = (marketPrice) => {

    const topRows = Array(9)
      .fill(null)
      .map((_, index) => ({
        price: generatePrice(
          marketPrice,
          -1,
          9 - index
        ),

        quantity: generateQuantity(marketPrice),

        priceExpires: 0,
        qtyExpires: 0,
        priceChange: false,
        qtyChange: false
      }));

    const bottomRows = Array(8)
      .fill(null)
      .map((_, index) => ({
        price: generatePrice(
          marketPrice,
          1,
          index + 1
        ),

        quantity: generateQuantity(marketPrice),

        priceExpires: 0,
        qtyExpires: 0,
        priceChange: false,
        qtyChange: false
      }));

    return {
      topRows,
      bottomRows
    };
  };


  const [orderData, setOrderData] = useState(() =>
    createRows(futureasset?.price)
  );


  /*
   * Rebuild order book whenever the REAL asset price changes.
   */
  useEffect(() => {

    if (!futureasset?.price) return;

    setOrderData(
      createRows(futureasset.price)
    );

  }, [
    futureasset?.price,
    futureasset?.symbol
  ]);


  /*
   * Animate the order book.
   *
   * IMPORTANT:
   * Instead of adding a fixed 0.01 / 0.08,
   * movement is calculated from the current price.
   */
  useEffect(() => {

    if (!futureasset?.price) return;

    const interval = setInterval(() => {

      setOrderData(prev => {

        const marketPrice = Number(futureasset.price);

        if (!Number.isFinite(marketPrice) || marketPrice <= 0) {
          return prev;
        }

        const now = Date.now();
        const HIGHLIGHT_MS = 700;

        const decimals = getDecimals(marketPrice);

        const updateRow = (row, direction) => {

          let currentPrice = Number(row.price);

          if (!Number.isFinite(currentPrice)) {
            currentPrice = marketPrice;
          }

          let currentQty = Number(row.quantity);

          if (!Number.isFinite(currentQty)) {
            currentQty = Number(generateQuantity(marketPrice));
          }

          let newPrice = currentPrice;
          let newQty = currentQty;

          let priceExpires = row.priceExpires || 0;
          let qtyExpires = row.qtyExpires || 0;

          /*
           * Randomly move the order.
           */
          if (Math.random() > 0.55) {

            const step = getPriceStep(marketPrice);

            const movement =
              step * (0.3 + Math.random() * 1.2);

            newPrice =
              currentPrice +
              (movement * direction);

            /*
             * Keep the order reasonably close
             * to the real market price.
             */
            const maxDistance = marketPrice * 0.002;

            if (
              Math.abs(newPrice - marketPrice) >
              maxDistance
            ) {
              newPrice =
                marketPrice +
                (direction * maxDistance *
                  (0.3 + Math.random() * 0.7));
            }

            newPrice = Math.max(
              newPrice,
              0.00000001
            );

            priceExpires = now + HIGHLIGHT_MS;
          }


          /*
           * Quantity changes independently.
           */
          if (Math.random() > 0.55) {

            const quantityStep =
              Math.max(
                currentQty * 0.03,
                0.00000001
              );

            newQty =
              currentQty +
              (
                quantityStep *
                (Math.random() * 2 - 1)
              );

            newQty = Math.max(
              newQty,
              0.00000001
            );

            qtyExpires = now + HIGHLIGHT_MS;
          }


          let quantityDecimals = 4;

          if (marketPrice < 1) {
            quantityDecimals = 6;
          }

          if (marketPrice < 0.1) {
            quantityDecimals = 8;
          }

          if (marketPrice < 0.001) {
            quantityDecimals = 10;
          }


          return {
            ...row,

            price: newPrice.toFixed(decimals),

            quantity: newQty.toFixed(
              quantityDecimals
            ),

            priceExpires,

            qtyExpires,

            priceChange:
              priceExpires > now,

            qtyChange:
              qtyExpires > now
          };
        };


        return {

          topRows: prev.topRows.map(
            row => updateRow(row, -1)
          ),

          bottomRows: prev.bottomRows.map(
            row => updateRow(row, 1)
          )

        };

      });

    }, 500);


    return () => clearInterval(interval);

  }, [
    futureasset?.price
  ]);


  /*
   * Main market price.
   */
  const [mainPrice, setMainPrice] = useState(
    Number(futureasset?.price || 0).toFixed(
      getDecimals(futureasset?.price)
    )
  );

  const [mainColor, setMainColor] = useState(
    Number(futureasset?.percentage) > 0
      ? 'success'
      : 'danger'
  );


  useEffect(() => {

    if (!futureasset) return;

    const decimals = getDecimals(
      futureasset.price
    );

    setMainPrice(
      Number(futureasset.price).toFixed(
        decimals
      )
    );

    setMainColor(
      Number(futureasset.percentage) > 0
        ? 'success'
        : 'danger'
    );

  }, [
    futureasset?.price,
    futureasset?.percentage
  ]);


  /*
   * Small visual movement of the displayed price.
   *
   * This also uses percentage-based movement.
   */
  useEffect(() => {

    if (!futureasset?.price) return;

    const interval = setInterval(() => {

      setMainPrice(prev => {

        const previousPrice = Number(prev);

        if (!Number.isFinite(previousPrice)) {
          return prev;
        }

        const step =
          getPriceStep(previousPrice);

        const movement =
          step * (Math.random() - 0.5);

        const newPrice =
          Math.max(
            previousPrice + movement,
            0.00000001
          );

        if (movement > 0) {
          setMainColor('success');
        } else if (movement < 0) {
          setMainColor('danger');
        }

        return newPrice.toFixed(
          getDecimals(futureasset.price)
        );

      });

    }, 2000);

    return () => clearInterval(interval);

  }, [
    futureasset?.price
  ]);


  const [buyPct, setBuyPct] = useState(50);

  useEffect(() => {

    const MIN = 30;
    const MAX = 70;

    const id = setInterval(() => {

      const newBuy =
        Math.floor(
          Math.random() *
          (MAX - MIN + 1)
        ) + MIN;

      setBuyPct(newBuy);

    }, 2000);

    return () => clearInterval(id);

  }, []);


  const sellPct = 100 - buyPct;


  return (
    <div className="mycard p-2 p-md-3 mt-2 orderBook">

      <p className="text-prime fw-bold mb-2">
        ORDERBOOK
      </p>

      <div className="d-flex align-items-center justify-content-between mb-1">

        <p
          className="mb-0 text-secondary"
          style={{
            fontSize: '14px',
            lineHeight: '110%'
          }}
        >
          Price (USDT)
        </p>

        <p
          className="mb-0 text-secondary text-end"
          style={{
            fontSize: '14px',
            lineHeight: '110%'
          }}
        >
          Quantity ({futureasset.symbol})
        </p>

      </div>


      {orderData.topRows.map((row, index) => (

        <div
          className="d-flex align-items-center justify-content-between fw-semibold"
          key={`top-${index}`}
        >

          <p
            className="mb-0 orderb text-danger w-50"
            style={
              row.priceChange
                ? {
                    backgroundColor: '#dc354540',
                    fontSize: '14px'
                  }
                : {
                    fontSize: '14px'
                  }
            }
          >
            {row.price}
          </p>

          <p
            className="mb-0 orderb text-danger w-50 text-end"
            style={
              row.qtyChange
                ? {
                    backgroundColor: '#dc354540',
                    fontSize: '14px'
                  }
                : {
                    fontSize: '14px'
                  }
            }
          >
            {row.quantity}
          </p>

        </div>

      ))}


      <div className="d-flex align-items-center justify-content-between my-2">

        <div>

          <p
            className={`mb-0 fs-4 text-${mainColor} dont`}
            style={{
              lineHeight: '120%',
              fontWeight: '900'
            }}
          >
            {mainPrice}
          </p>

          <p
            className="mb-0 fw-semibold lh-1 dont1"
            style={{
              fontSize: '13px'
            }}
          >
            {mainPrice}
          </p>

        </div>

        <FontAwesomeIcon
          icon={faArrowRight}
          style={{
            fontSize: '16px'
          }}
        />

      </div>


      {orderData.bottomRows.map((row, index) => (

        <div
          className="d-flex align-items-center justify-content-between fw-semibold"
          key={`bottom-${index}`}
        >

          <p
            className="mb-0 orderb text-success w-50"
            style={
              row.priceChange
                ? {
                    backgroundColor: '#1987546b',
                    fontSize: '14px'
                  }
                : {
                    fontSize: '14px'
                  }
            }
          >
            {row.price}
          </p>

          <p
            className="mb-0 orderb text-success w-50 text-end"
            style={
              row.qtyChange
                ? {
                    backgroundColor: '#1987546b',
                    fontSize: '14px'
                  }
                : {
                    fontSize: '14px'
                  }
            }
          >
            {row.quantity}
          </p>

        </div>

      ))}


      <div className="d-flex align-items-center mt-md-3 mt-2 position-relative">

        <div
          className="position-absolute rounded-2 border border-2 border-success d-flex align-items-center justify-content-center fw-bold text-success orderBox"
          style={{
            left: '-5px',
            backgroundColor: '#111111'
          }}
        >
          B
        </div>

        <div
          className="position-absolute rounded-2 border border-2 border-danger d-flex align-items-center justify-content-center fw-bold text-danger orderBox"
          style={{
            right: '-5px',
            backgroundColor: '#111111'
          }}
        >
          S
        </div>

        <div
          className="orderBoxInner"
          style={{
            width: `${buyPct}%`,
            backgroundColor:
              'rgba(25, 135, 84, 0.42)'
          }}
        >
          <p className="mb-0 ms-md-5 ms-4 text-success fw-semibold">
            {buyPct}%
          </p>
        </div>

        <div
          className="orderBoxInner"
          style={{
            width: `${sellPct}%`,
            backgroundColor:
              'rgba(220, 53, 69, 0.25)'
          }}
        >
          <p className="mb-0 me-md-5 me-4 text-danger text-end fw-semibold">
            {sellPct}%
          </p>
        </div>

      </div>

    </div>
  );
}

export default OrderBook;